import { useMemo } from "react";
import { mergeClasses } from "@fluentui/react-components";
import {
  RAID_CATEGORY_PILL_LABEL,
  RAID_LIFECYCLE_STATUS_TONE,
  RAID_SEVERITY_TONE,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.constants";
import { dashboardTokens } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.tokens";
import {
  IRaidLogTableProps,
  IReadOnlyTableColumn,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.types";
import {
  getPillToneClassName,
  useGroupedWorkspaceDashboardStyles,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.styles";
import { LogEmptyState } from "apps/project-iq/components/GroupedWorkspaceDashboard/LogEmptyState";
import { ReadOnlyLogTable } from "apps/project-iq/components/GroupedWorkspaceDashboard/ReadOnlyLogTable";
import { IRaidEntry } from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import { formatDate } from "apps/project-iq/utils/workspaceOverviewUtils";

export const RaidLogTable = ({
  raidEntries,
}: IRaidLogTableProps): JSX.Element => {
  const styles = useGroupedWorkspaceDashboardStyles();

  const columns: IReadOnlyTableColumn<IRaidEntry>[] = useMemo(
    () => [
      {
        columnKey: "name",
        header: "Name",
        renderCell: (item) => (
          <span className={styles.logPanelNameRaid}>{item.name}</span>
        ),
      },
      {
        columnKey: "type",
        header: "Type",
        renderCell: (item) => (
          <span
            className={mergeClasses(
              styles.pill,
              getPillToneClassName(
                styles,
                dashboardTokens.status.raidTypeNeutral.toneKey,
              ),
            )}
          >
            {RAID_CATEGORY_PILL_LABEL[item.category]}
          </span>
        ),
      },
      {
        columnKey: "severity",
        header: "Severity",
        renderCell: (item) => {
          const tone = RAID_SEVERITY_TONE[item.severity];
          return (
            <span
              className={mergeClasses(
                styles.pill,
                getPillToneClassName(styles, tone?.toneKey),
              )}
            >
              {tone?.label}
            </span>
          );
        },
      },
      {
        columnKey: "dueDate",
        header: "Due Date",
        renderCell: (item) => (
          <span className={styles.logPanelCellMuted}>
            {item.dueDate ? formatDate(item.dueDate) : "—"}
          </span>
        ),
      },
      {
        columnKey: "status",
        header: "Status",
        renderCell: (item) => {
          const tone = RAID_LIFECYCLE_STATUS_TONE[item.status];
          return (
            <span
              className={mergeClasses(
                styles.pill,
                getPillToneClassName(styles, tone?.toneKey),
              )}
            >
              {tone?.label}
            </span>
          );
        },
      },
    ],
    [styles],
  );

  return (
    <ReadOnlyLogTable<IRaidEntry>
      items={raidEntries}
      columns={columns}
      getRowId={(item) => item.id}
      ariaLabel="RAID Log"
      gridTemplateColumns={dashboardTokens.grid.raid}
      emptyState={<LogEmptyState logTab="raid" />}
    />
  );
};
